<template>
  <div class="bonuses-page">
    <h1 class="page-title">{{ $t('bonuses.title') }}</h1>

    <!-- TABS -->
    <div class="bonus-tabs">
      <button
        class="bonus-tab"
        :class="{ active: activeTab === 'available' }"
        @click="activeTab = 'available'"
      >
        {{ $t('bonuses.tab_available') }}
      </button>
      <button
        class="bonus-tab"
        :class="{ active: activeTab === 'active' }"
        @click="activeTab = 'active'"
      >
        {{ $t('bonuses.tab_active') }}
      </button>
      <button
        class="bonus-tab"
        :class="{ active: activeTab === 'history' }"
        @click="activeTab = 'history'"
      >
        {{ $t('bonuses.tab_history') }}
      </button>
    </div>

    <!-- AVAILABLE TAB -->
    <div v-if="activeTab === 'available'" class="tab-pane">
      <!-- Loading -->
      <div v-if="bonusStore.loadingAvailable" class="loading-grid">
        <div v-for="i in 4" :key="i" class="bonus-card-skeleton">
          <div class="skel-header"></div>
          <div class="skel-body">
            <div class="skel-line wide"></div>
            <div class="skel-line"></div>
            <div class="skel-line"></div>
          </div>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="bonusStore.error && !bonusStore.availableBonuses.length" class="error-state">
        <p>{{ bonusStore.error }}</p>
        <button class="btn-retry" @click="bonusStore.fetchAvailable()">{{ $t('common.try_again') }}</button>
      </div>

      <!-- Empty state -->
      <div v-else-if="!bonusStore.loadingAvailable && !bonusStore.availableBonuses.length" class="empty-state">
        <div class="empty-icon"><AppIcon name="empty-state-bonus" :size="48" /></div>
        <p class="empty-title">{{ $t('bonuses.no_bonuses') }}</p>
        <p class="empty-sub">{{ $t('bonuses.no_bonuses_sub') }}</p>
      </div>

      <!-- Bonuses Grid -->
      <div v-else class="bonuses-grid">
        <div
          v-for="bonus in bonusStore.availableBonuses"
          :key="bonus.id"
          class="bonus-card"
          :class="{ 'not-eligible': !bonus.eligible }"
        >
          <div class="bonus-card-header" :class="headerClass(bonus.type)">
            <div>
              <span class="bonus-badge" :class="badgeClass(bonus.type)">{{ typeLabel(bonus.type) }}</span>
              <h3 class="bonus-card-title">{{ bonus.name }}</h3>
            </div>
            <span v-if="bonus.expires_at" class="bonus-expiry">{{ expiryLabel(bonus.expires_at) }}</span>
            <span v-else class="bonus-expiry">{{ $t('bonuses.ongoing') }}</span>
          </div>

          <div class="bonus-card-body">
            <div class="bonus-value" :class="valueClass(bonus.type)">
              {{ bonusValueDisplay(bonus) }}
            </div>
            <p v-if="bonus.description" class="bonus-description">{{ bonus.description }}</p>

            <div class="bonus-details">
              <div v-if="bonus.wagering_multiplier" class="bonus-detail">
                <span class="bonus-detail-label">{{ $t('bonuses.wagering_requirement') }}</span>
                <span class="bonus-detail-value">{{ bonus.wagering_multiplier }}x</span>
              </div>
              <div v-if="bonus.max_cashout" class="bonus-detail">
                <span class="bonus-detail-label">{{ $t('bonuses.max_cashout') }}</span>
                <span class="bonus-detail-value">{{ formatCents(bonus.max_cashout) }}</span>
              </div>
              <div v-if="bonus.expires_at" class="bonus-detail">
                <span class="bonus-detail-label">{{ $t('bonuses.expires') }}</span>
                <span class="bonus-detail-value">{{ formatDate(bonus.expires_at) }}</span>
              </div>
            </div>
          </div>

          <div class="bonus-card-footer">
            <button
              class="btn-claim"
              :disabled="!bonus.eligible || claimingId === bonus.id"
              @click="handleClaim(bonus.id)"
            >
              <span v-if="claimingId === bonus.id">{{ $t('bonuses.claiming') }}</span>
              <span v-else-if="!bonus.eligible">{{ $t('bonuses.not_eligible') }}</span>
              <span v-else>{{ $t('bonuses.claim_bonus') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- PROMO CODE SECTION -->
      <div class="promo-box">
        <h3 class="promo-title">{{ $t('bonuses.promo_title') }}</h3>
        <p class="promo-desc">{{ $t('bonuses.promo_desc') }}</p>
        <div class="promo-input-group">
          <input
            v-model="promoCode"
            type="text"
            class="promo-input"
            :placeholder="$t('bonuses.promo_placeholder')"
            maxlength="30"
            :disabled="bonusStore.loadingRedeem"
            @keydown.enter="handleRedeem"
          >
          <button
            class="btn-redeem"
            :disabled="bonusStore.loadingRedeem || !promoCode.trim()"
            @click="handleRedeem"
          >
            <span v-if="bonusStore.loadingRedeem">{{ $t('bonuses.redeeming') }}</span>
            <span v-else>{{ $t('bonuses.redeem_code') }}</span>
          </button>
        </div>
        <div v-if="promoMessage" class="promo-msg" :class="promoMessageType">
          {{ promoMessage }}
        </div>
      </div>
    </div>

    <!-- ACTIVE TAB -->
    <div v-if="activeTab === 'active'" class="tab-pane">
      <!-- Loading -->
      <div v-if="bonusStore.loadingActive" class="active-loading">
        <div v-for="i in 2" :key="i" class="active-bonus-skeleton">
          <div class="skel-line wide" style="height: 22px; margin-bottom: 12px;"></div>
          <div class="skel-line" style="height: 14px; width: 40%;"></div>
          <div class="skel-progress" style="height: 8px; margin-top: 20px;"></div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="!bonusStore.activeBonuses.length" class="empty-state">
        <div class="empty-icon">⏳</div>
        <p class="empty-title">{{ $t('bonuses.no_active') }}</p>
        <p class="empty-sub">{{ $t('bonuses.no_active_sub') }}</p>
      </div>

      <!-- Active bonus cards -->
      <div v-else>
        <div
          v-for="bonus in bonusStore.activeBonuses"
          :key="bonus.id"
          class="active-bonus"
        >
          <div class="active-bonus-header">
            <div>
              <h3 class="active-bonus-title">{{ bonus.bonus_name }}</h3>
              <p class="active-bonus-amount">{{ formatCents(bonus.bonus_amount) }} {{ $t('bonuses.bonus_suffix') }}</p>
            </div>
            <div class="active-bonus-header-right">
              <span class="status-badge">{{ $t('bonuses.active_status') }}</span>
              <button
                class="btn-forfeit"
                :disabled="forfeitingId === bonus.id"
                @click="handleForfeit(bonus.id)"
              >
                {{ forfeitingId === bonus.id ? $t('bonuses.forfeiting') : $t('bonuses.forfeit') }}
              </button>
            </div>
          </div>

          <div class="active-bonus-stats">
            <div class="stat-item">
              <span class="stat-label">{{ $t('bonuses.bonus_amount') }}</span>
              <span class="stat-value">{{ formatCents(bonus.bonus_amount) }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">{{ $t('bonuses.wagered_so_far') }}</span>
              <span class="stat-value">{{ formatCentsStr(bonus.wagering_completed) }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">{{ $t('bonuses.required_wagering') }}</span>
              <span class="stat-value">{{ formatCentsStr(bonus.wagering_required) }}</span>
            </div>
          </div>

          <div class="wagering-progress">
            <div class="progress-header">
              <span class="progress-label">{{ $t('bonuses.wagering_progress') }}</span>
              <span class="progress-pct">{{ bonus.progress_percent }}%</span>
            </div>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: `${Math.min(bonus.progress_percent, 100)}%` }"
              ></div>
            </div>
            <div class="progress-info">
              <span>{{ formatCentsStr(bonus.wagering_completed) }} / {{ formatCentsStr(bonus.wagering_required) }} {{ $t('bonuses.wagered_label') }}</span>
              <span v-if="bonus.expires_at">{{ expiryLabel(bonus.expires_at) }}</span>
              <span v-else>{{ $t('bonuses.no_expiry') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- HISTORY TAB -->
    <div v-if="activeTab === 'history'" class="tab-pane">
      <!-- Loading -->
      <div v-if="bonusStore.loadingHistory" class="table-wrapper">
        <div class="history-loading">
          <div v-for="i in 5" :key="i" class="history-row-skel">
            <div class="skel-line" style="width: 30%;"></div>
            <div class="skel-line" style="width: 15%;"></div>
            <div class="skel-line" style="width: 12%;"></div>
            <div class="skel-line" style="width: 20%;"></div>
            <div class="skel-line" style="width: 15%;"></div>
            <div class="skel-line" style="width: 12%;"></div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="!bonusStore.bonusHistory.length" class="empty-state">
        <div class="empty-icon">🕒</div>
        <p class="empty-title">{{ $t('bonuses.no_history') }}</p>
        <p class="empty-sub">{{ $t('bonuses.no_history_sub') }}</p>
      </div>

      <!-- History table -->
      <div v-else>
        <div class="table-wrapper">
          <table class="history-table">
            <thead>
              <tr>
                <th>{{ $t('bonuses.col_bonus') }}</th>
                <th>{{ $t('bonuses.col_type') }}</th>
                <th>{{ $t('bonuses.col_amount') }}</th>
                <th>{{ $t('bonuses.col_wagering') }}</th>
                <th>{{ $t('bonuses.col_claimed') }}</th>
                <th>{{ $t('bonuses.col_status') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in bonusStore.bonusHistory" :key="item.id">
                <td class="bonus-name">{{ item.bonus_name }}</td>
                <td>
                  <span class="bonus-badge" :class="badgeClass(item.type)">{{ typeLabel(item.type) }}</span>
                </td>
                <td class="bonus-amount-cell">{{ formatCents(item.bonus_amount) }}</td>
                <td>{{ formatCentsStr(item.wagering_completed) }} / {{ formatCentsStr(item.wagering_required) }}</td>
                <td>{{ item.activated_at ? formatDate(item.activated_at) : '—' }}</td>
                <td>
                  <span class="bonus-status" :class="statusBadgeClass(item.status)">
                    {{ statusLabel(item.status) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="bonusStore.historyMeta.last_page > 1" class="pagination">
          <button
            class="pagination-btn"
            :disabled="bonusStore.historyMeta.current_page <= 1"
            @click="changePage(bonusStore.historyMeta.current_page - 1)"
          >
            {{ $t('common.pagination_prev') }}
          </button>
          <span class="pagination-info">
            {{ $t('common.page_of', { current: bonusStore.historyMeta.current_page, total: bonusStore.historyMeta.last_page }) }}
          </span>
          <button
            class="pagination-btn"
            :disabled="bonusStore.historyMeta.current_page >= bonusStore.historyMeta.last_page"
            @click="changePage(bonusStore.historyMeta.current_page + 1)"
          >
            {{ $t('common.pagination_next') }}
          </button>
        </div>
      </div>
    </div>

    <!-- FORFEIT CONFIRMATION MODAL -->
    <div v-if="forfeitModalOpen" class="modal-overlay" @click.self="cancelForfeit">
      <div class="modal-box">
        <h3 class="modal-title">{{ $t('bonuses.forfeit_modal_title') }}</h3>
        <p class="modal-body">{{ $t('bonuses.forfeit_modal_body') }}</p>
        <div class="modal-actions">
          <button class="btn-modal-cancel" @click="cancelForfeit">{{ $t('common.cancel') }}</button>
          <button
            class="btn-modal-confirm"
            :disabled="bonusStore.loadingForfeit"
            @click="confirmForfeit"
          >
            <span v-if="bonusStore.loadingForfeit">{{ $t('bonuses.forfeiting') }}</span>
            <span v-else>{{ $t('bonuses.confirm_forfeit') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBonusStore } from '@/stores/bonus'
import AppIcon from '@/components/AppIcon.vue'
import type { AvailableBonus } from '@/stores/bonus'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()
const bonusStore = useBonusStore()
const toastStore = useToastStore()

const activeTab = ref<'available' | 'active' | 'history'>('available')
const claimingId = ref<number | null>(null)
const forfeitingId = ref<number | null>(null)
const forfeitModalOpen = ref(false)
const forfeitTargetId = ref<number | null>(null)
const promoCode = ref('')
const promoMessage = ref('')
const promoMessageType = ref<'success' | 'error'>('success')

onMounted(() => {
  bonusStore.fetchAvailable()
})

// Load data when switching tabs
watch(activeTab, (newTab) => {
  if (newTab === 'active') {
    bonusStore.fetchActive()
  } else if (newTab === 'history') {
    bonusStore.fetchHistory(1)
  }
})

// --- Claim ---
async function handleClaim(bonusId: number): Promise<void> {
  claimingId.value = bonusId
  try {
    await bonusStore.claimBonus(bonusId)
    toastStore.success(t('bonuses.claim_success'))
    await Promise.all([bonusStore.fetchAvailable(), bonusStore.fetchActive()])
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    const msg = axiosErr.response?.data?.message ?? t('bonuses.claim_failed')
    toastStore.error(msg)
  } finally {
    claimingId.value = null
  }
}

// --- Promo code ---
async function handleRedeem(): Promise<void> {
  const code = promoCode.value.trim()
  if (!code) return

  promoMessage.value = ''
  try {
    await bonusStore.redeemCode(code)
    promoMessage.value = t('bonuses.promo_redeemed_msg')
    promoMessageType.value = 'success'
    promoCode.value = ''
    await Promise.all([bonusStore.fetchAvailable(), bonusStore.fetchActive()])
    toastStore.success(t('bonuses.promo_redeemed_toast'))
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    const msg = axiosErr.response?.data?.message ?? t('bonuses.promo_error_fallback')
    promoMessage.value = `✗ ${msg}`
    promoMessageType.value = 'error'
  }
}

// --- Forfeit ---
function handleForfeit(userBonusId: number): void {
  forfeitTargetId.value = userBonusId
  forfeitModalOpen.value = true
}

function cancelForfeit(): void {
  forfeitModalOpen.value = false
  forfeitTargetId.value = null
}

async function confirmForfeit(): Promise<void> {
  const id = forfeitTargetId.value
  if (id === null) return

  forfeitingId.value = id
  forfeitModalOpen.value = false
  forfeitTargetId.value = null

  try {
    await bonusStore.forfeitBonus(id)
    toastStore.success(t('bonuses.forfeit_success'))
    await Promise.all([bonusStore.fetchActive(), bonusStore.fetchHistory(1)])
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    const msg = axiosErr.response?.data?.message ?? t('bonuses.forfeit_failed')
    toastStore.error(msg)
  } finally {
    forfeitingId.value = null
  }
}

// --- History pagination ---
async function changePage(page: number): Promise<void> {
  await bonusStore.fetchHistory(page)
}

// --- Display helpers ---
function typeLabel(type: string): string {
  const labels: Record<string, string> = {
    welcome: t('bonuses.type_welcome'),
    deposit: t('bonuses.type_deposit'),
    no_deposit: t('bonuses.type_no_deposit'),
    free_spins: t('bonuses.type_free_spins'),
    cashback: t('bonuses.type_cashback'),
    reload: t('bonuses.type_reload'),
    promo_code: t('bonuses.type_promo_code'),
  }
  return labels[type] ?? type
}

function headerClass(type: string): string {
  const classes: Record<string, string> = {
    welcome: 'welcome',
    deposit: 'welcome',
    reload: 'reload',
    free_spins: 'spins',
    cashback: 'cashback',
    no_deposit: 'reload',
    promo_code: 'spins',
  }
  return classes[type] ?? ''
}

function badgeClass(type: string): string {
  const classes: Record<string, string> = {
    welcome: 'badge-welcome',
    deposit: 'badge-welcome',
    reload: 'badge-reload',
    free_spins: 'badge-spins',
    cashback: 'badge-cashback',
    no_deposit: 'badge-reload',
    promo_code: 'badge-spins',
  }
  return classes[type] ?? 'badge-reload'
}

function valueClass(type: string): string {
  const classes: Record<string, string> = {
    welcome: 'green',
    deposit: 'green',
    reload: 'blue',
    free_spins: 'gold',
    cashback: 'red',
    no_deposit: 'green',
    promo_code: 'blue',
  }
  return classes[type] ?? 'green'
}

function statusBadgeClass(status: string): string {
  const classes: Record<string, string> = {
    active: 'status-active',
    completed: 'status-completed',
    expired: 'status-expired',
    forfeited: 'status-forfeited',
  }
  return classes[status] ?? 'status-expired'
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    active: t('bonuses.status_active'),
    completed: t('bonuses.status_completed'),
    expired: t('bonuses.status_expired'),
    forfeited: t('bonuses.status_forfeited'),
  }
  return labels[status] ?? status
}

function bonusValueDisplay(bonus: AvailableBonus): string {
  if (bonus.type === 'free_spins' && bonus.bonus_amount !== null) {
    return `${formatCents(bonus.bonus_amount)} ${t('bonuses.in_spins')}`
  }
  if (bonus.percentage !== null) {
    const pct = bonus.percentage
    const cap = bonus.bonus_amount !== null ? ` ${t('bonuses.up_to')} ${formatCents(bonus.bonus_amount)}` : ''
    return `${pct}%${cap}`
  }
  if (bonus.bonus_amount !== null) {
    return formatCents(bonus.bonus_amount)
  }
  return '—'
}

function formatCents(cents: number): string {
  const amount = cents / 100
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatCentsStr(centsStr: string): string {
  return formatCents(parseFloat(centsStr))
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function expiryLabel(iso: string): string {
  const now = Date.now()
  const end = new Date(iso).getTime()
  const diffMs = end - now
  if (diffMs <= 0) return t('bonuses.expiry_expired')
  const days = Math.floor(diffMs / 86_400_000)
  if (days > 1) return t('bonuses.expiry_days_left', { n: days })
  if (days === 1) return t('bonuses.expiry_day_left')
  const hours = Math.floor(diffMs / 3_600_000)
  return t('bonuses.expiry_hours_left', { n: hours })
}
</script>

<style scoped>
.bonuses-page {
  padding: 24px;
  max-width: 960px;
  margin: 0 auto;
  animation: fadeUp 0.3s ease both;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.page-title {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 28px;
  margin-bottom: 24px;
  color: var(--text-primary, #fff);
}

/* TABS */
.bonus-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  margin-bottom: 24px;
}

.bonus-tab {
  padding: 12px 20px;
  background: none;
  border: none;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
  border-bottom: 2px solid transparent;
  position: relative;
  bottom: -1px;
  font-family: inherit;
}

.bonus-tab.active {
  color: var(--accent-primary, #00e701);
  border-bottom-color: var(--accent-primary, #00e701);
}

.bonus-tab:hover:not(.active) {
  color: var(--text-primary, #fff);
}

/* TAB PANE */
.tab-pane {
  animation: fadeUp 0.25s ease both;
}

/* GRID */
.bonuses-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 28px;
}

/* BONUS CARD */
.bonus-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
  transition: all 250ms ease;
}

.bonus-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.12);
}

.bonus-card.not-eligible {
  opacity: 0.65;
}

.bonus-card-header {
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.bonus-card-header.welcome {
  background: linear-gradient(135deg, rgba(0, 231, 1, 0.08) 0%, rgba(20, 117, 225, 0.08) 100%);
}

.bonus-card-header.reload {
  background: linear-gradient(135deg, rgba(20, 117, 225, 0.08) 0%, rgba(155, 89, 182, 0.08) 100%);
}

.bonus-card-header.spins {
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.08) 0%, rgba(255, 167, 38, 0.08) 100%);
}

.bonus-card-header.cashback {
  background: linear-gradient(135deg, rgba(237, 66, 69, 0.08) 0%, rgba(255, 167, 38, 0.08) 100%);
}

/* BADGES */
.bonus-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.badge-welcome {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-reload {
  background: rgba(20, 117, 225, 0.15);
  color: var(--color-info, #1475e1);
}

.badge-spins {
  background: rgba(255, 215, 0, 0.15);
  color: var(--accent-gold, #ffd700);
}

.badge-cashback {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.bonus-card-title {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 18px;
  margin-top: 8px;
  color: var(--text-primary, #fff);
}

.bonus-expiry {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  background: rgba(255, 255, 255, 0.05);
  padding: 4px 10px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* CARD BODY */
.bonus-card-body {
  padding: 20px;
}

.bonus-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 10px;
}

.bonus-value.green { color: var(--color-success, #00e701); }
.bonus-value.blue  { color: var(--color-info, #1475e1); }
.bonus-value.gold  { color: var(--accent-gold, #ffd700); }
.bonus-value.red   { color: var(--color-danger, #ed4245); }

.bonus-description {
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 14px;
}

.bonus-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bonus-detail {
  display: flex;
  justify-content: space-between;
  padding: 7px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
}

.bonus-detail:last-child {
  border-bottom: none;
}

.bonus-detail-label { color: var(--text-tertiary, #7f8fa4); }
.bonus-detail-value { color: var(--text-primary, #fff); font-weight: 500; }

/* CARD FOOTER */
.bonus-card-footer {
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.btn-claim {
  width: 100%;
  padding: 10px 16px;
  background: var(--accent-primary, #00e701);
  color: #000;
  font-size: 13px;
  font-weight: 700;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}

.btn-claim:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

.btn-claim:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
}

/* PROMO CODE */
.promo-box {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 24px;
  margin-top: 8px;
}

.promo-title {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 18px;
  margin-bottom: 6px;
  color: var(--text-primary, #fff);
}

.promo-desc {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 16px;
}

.promo-input-group {
  display: flex;
  gap: 12px;
}

.promo-input {
  flex: 1;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-primary, #fff);
  padding: 11px 16px;
  border-radius: 8px;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 13px;
  text-transform: uppercase;
  transition: border-color 150ms ease;
}

.promo-input:focus {
  outline: none;
  border-color: rgba(0, 231, 1, 0.3);
}

.promo-input:disabled {
  opacity: 0.5;
}

.btn-redeem {
  padding: 11px 24px;
  background: var(--accent-primary, #00e701);
  color: #000;
  font-weight: 700;
  font-size: 13px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
  white-space: nowrap;
}

.btn-redeem:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
}

.btn-redeem:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.promo-msg {
  font-size: 13px;
  margin-top: 10px;
  font-weight: 500;
}

.promo-msg.success { color: var(--color-success, #00e701); }
.promo-msg.error   { color: var(--color-danger, #ed4245); }

/* LOADING SKELETON */
.loading-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 28px;
}

.bonus-card-skeleton {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
}

.skel-header {
  height: 80px;
  background: linear-gradient(90deg, rgba(255,255,255,.04) 25%, rgba(255,255,255,.08) 50%, rgba(255,255,255,.04) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skel-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skel-line {
  height: 14px;
  border-radius: 4px;
  width: 60%;
  background: linear-gradient(90deg, rgba(255,255,255,.04) 25%, rgba(255,255,255,.08) 50%, rgba(255,255,255,.04) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skel-line.wide { width: 80%; }

@keyframes shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* EMPTY / ERROR STATES */
.empty-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  gap: 10px;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 6px;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.empty-sub {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

.btn-retry {
  margin-top: 8px;
  padding: 9px 20px;
  background: var(--accent-primary, #00e701);
  color: #000;
  font-size: 13px;
  font-weight: 700;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-family: inherit;
}

/* ACTIVE BONUSES */
.active-loading {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.active-bonus-skeleton {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 24px;
}

.skel-progress {
  width: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(255,255,255,.04) 25%, rgba(255,255,255,.08) 50%, rgba(255,255,255,.04) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.active-bonus {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(0, 231, 1, 0.15);
  border-left: 4px solid var(--accent-primary, #00e701);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  transition: all 250ms ease;
}

.active-bonus:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border-color: rgba(0, 231, 1, 0.25);
}

.active-bonus-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.active-bonus-title {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 18px;
  color: var(--text-primary, #fff);
}

.active-bonus-amount {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 4px;
}

.active-bonus-header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.status-badge {
  padding: 4px 12px;
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
  font-size: 11px;
  font-weight: 700;
  border-radius: 9999px;
  text-transform: uppercase;
  white-space: nowrap;
}

.btn-forfeit {
  padding: 6px 14px;
  background: transparent;
  color: var(--color-danger, #ed4245);
  font-size: 12px;
  font-weight: 600;
  border: 1px solid rgba(237, 66, 69, 0.3);
  border-radius: 6px;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
  white-space: nowrap;
}

.btn-forfeit:hover:not(:disabled) {
  background: rgba(237, 66, 69, 0.1);
  border-color: rgba(237, 66, 69, 0.6);
}

.btn-forfeit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.active-bonus-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin: 16px 0;
}

.stat-item {
  display: flex;
  flex-direction: column;
}

.stat-label {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}

.stat-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.wagering-progress {
  margin-top: 4px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
}

.progress-label {
  color: var(--text-secondary, #b1bad3);
}

.progress-pct {
  color: var(--text-primary, #fff);
  font-weight: 600;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: var(--bg-tertiary, #213743);
  border-radius: 9999px;
  overflow: hidden;
  margin: 4px 0;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--accent-primary, #00e701), #00e701);
  border-radius: 9999px;
  transition: width 500ms ease;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 8px;
}

/* HISTORY TABLE */
.table-wrapper {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
}

.history-loading {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.history-row-skel {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 8px 0;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
}

.history-table th {
  background: var(--bg-tertiary, #213743);
  padding: 14px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.history-table td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

.history-table tbody tr:hover {
  background: rgba(0, 231, 1, 0.02);
}

.history-table tbody tr:last-child td {
  border-bottom: none;
}

.bonus-name {
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.bonus-amount-cell {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.bonus-status {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

.status-active    { background: rgba(20, 117, 225, 0.15);  color: var(--color-info, #1475e1); }
.status-completed { background: rgba(0, 231, 1, 0.15);     color: var(--color-success, #00e701); }
.status-expired   { background: rgba(127, 143, 164, 0.15); color: var(--text-tertiary, #7f8fa4); }
.status-forfeited { background: rgba(237, 66, 69, 0.15);   color: var(--color-danger, #ed4245); }

/* PAGINATION */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px 0;
}

.pagination-btn {
  padding: 8px 18px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled) {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.25);
  background: var(--bg-tertiary, #213743);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

/* FORFEIT MODAL */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 150ms ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.modal-box {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 32px;
  max-width: 420px;
  width: 90%;
  animation: fadeUp 200ms ease;
}

.modal-title {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 20px;
  color: var(--text-primary, #fff);
  margin-bottom: 12px;
}

.modal-body {
  font-size: 14px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.6;
  margin-bottom: 24px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-modal-cancel {
  padding: 10px 20px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}

.btn-modal-cancel:hover {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.25);
  background: var(--bg-tertiary, #213743);
}

.btn-modal-confirm {
  padding: 10px 20px;
  background: var(--color-danger, #ed4245);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}

.btn-modal-confirm:hover:not(:disabled) {
  background: #c5333a;
}

.btn-modal-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* RESPONSIVE */
@media (max-width: 1100px) {
  .bonuses-grid,
  .loading-grid {
    grid-template-columns: 1fr;
  }

  .active-bonus-stats {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

@media (max-width: 640px) {
  .bonuses-page {
    padding: 14px;
  }

  .page-title {
    font-size: 22px;
  }

  .promo-input-group {
    flex-direction: column;
  }

  .history-table {
    font-size: 12px;
  }

  .history-table th,
  .history-table td {
    padding: 10px 10px;
  }
}
</style>
